import type { DatasetBase } from '@core/reports-types';
import type { ValueFormatterOptions } from '@core/utils-legacy';
import { resolveDurationScale } from '@core/utils-legacy';

import { isNetChangeMetric } from './net-change';

const cache = new WeakMap<DatasetBase, ValueFormatterOptions>();

/**
 * Largest duration a visualization built from this dataset can end up showing:
 * either a single cell, or a stacked/summed row. Only positive values are
 * summed — signed datasets would otherwise inflate the total by counting a
 * negative column twice.
 *
 * Net change is never stacked: its columns diverge from a shared baseline and
 * the third one is the backend computed net of the other two, so summing them
 * would count the same duration twice over. Only the largest single cell can
 * ever be rendered there.
 */
const maxDisplayedValue = (dataset: DatasetBase) => {
  const stacks = !isNetChangeMetric(dataset.metric);

  return dataset
    .getRawData()
    .slice(1)
    .reduce((max, row) => {
      let positiveTotal = 0;
      let rowMax = 0;

      for (const cell of row.slice(1)) {
        if (typeof cell !== 'number' || !Number.isFinite(cell)) continue;

        const magnitude = Math.abs(cell);
        if (magnitude > rowMax) rowMax = magnitude;
        if (cell > 0) positiveTotal += cell;
      }

      return Math.max(max, rowMax, stacks ? positiveTotal : 0);
    }, 0);
};

/**
 * Formatter options for every label of one visualization. The duration unit
 * family is derived from the dataset as a whole rather than from each value, so
 * bars, axis ticks and tooltips cannot disagree on the unit.
 *
 * Memoized per dataset instance: call sites format value by value, and scanning
 * the data on each of them would be quadratic.
 */
export const datasetValueFormatterOptions = (dataset: DatasetBase): ValueFormatterOptions => {
  const cached = cache.get(dataset);
  if (cached) return cached;

  const unitPrecision = dataset?.meta?.unitPrecision ?? dataset.metric.unitPrecision;
  const resolvesFamilyFromData =
    dataset.metric.unitType === 'duration' && unitPrecision !== 'years';
  const options: ValueFormatterOptions = {
    unitType: dataset.metric.unitType,
    unitPrecision,
    unitDisplay: 'narrow',
    ratingScale: dataset.metric.ratingScale,
    durationScale: resolvesFamilyFromData
      ? resolveDurationScale(maxDisplayedValue(dataset) * (unitPrecision === 'minutes' ? 60 : 1))
      : undefined,
  };

  cache.set(dataset, options);

  return options;
};

/**
 * Same options, with the unit spelled out in full - for the places that lay the
 * value out as text and can wrap or scroll it: table cells and tooltips. Chart
 * geometry and the summary headline (a scaled SVG number) cannot, and keep the
 * base options.
 */
export const datasetTextFormatterOptions = (dataset: DatasetBase): ValueFormatterOptions => ({
  ...datasetValueFormatterOptions(dataset),
  unitDisplay: 'long',
});

/** Same options, plus the exact day count that only fits in a tooltip. */
export const datasetTooltipFormatterOptions = (dataset: DatasetBase): ValueFormatterOptions => ({
  ...datasetTextFormatterOptions(dataset),
  withExactDays: true,
});

/**
 * Same options, minus the scale-range suffix on score values ("3.4" instead of
 * "3.4/5") — on-chart labels and axis ticks would repeat it on every bar and
 * tick. Tables and tooltips keep the full form.
 */
export const datasetChartLabelFormatterOptions = (dataset: DatasetBase): ValueFormatterOptions => ({
  ...datasetValueFormatterOptions(dataset),
  bareScaleValue: true,
});

export const isCalendarScaleDataset = (dataset: DatasetBase) =>
  datasetValueFormatterOptions(dataset).durationScale === 'calendar';
